/**
 * TelegramGate — post-login hard gate for every non-admin role.
 *
 * Rules (set by the user in iter 64):
 *   - Admins bypass everything.
 *   - All other eligible users must (i) have a linked Telegram chat AND
 *     (ii) be a current member of every active reporting group they're
 *     assigned to (via the manual `members` list OR the role-based
 *     `requiredRoles` expansion).
 *   - The check fires ONCE per login (mounted at the top of the
 *     authenticated tree). User chose login-only re-check; we don't
 *     poll the gate while the tab stays open.
 *
 * UX: when blocked, we render a FULL-SCREEN page over the app. The user
 * can either (a) link their Telegram via the existing TelegramLinkButton
 * dialog, or (b) join each missing reporting group via the invite link
 * cards. We re-check via the `/telegram/gate-status` endpoint after
 * either action; once the response flips to `blocked: false`, we
 * unmount the gate and the app appears.
 *
 * Routes that MUST stay reachable while gated: `/login` (so users can
 * log out + back in). Everything else is hidden.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Send, Users, ExternalLink, Loader2, LogOut, CheckCircle2, ShieldAlert } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { API } from '@/lib/api';
import LanguageToggle from '@/components/shared/LanguageToggle';
import { useT } from '@/i18n';

const TelegramGate = ({ children }) => {
  const { user, logout } = useAuth();
  const { t } = useT();
  const [status, setStatus] = useState({
    loading: true, blocked: false, needsLink: false,
    missingGroups: [], joinedGroups: [], unknownGroups: [],
    joinedCount: 0, totalRequired: 0,
  });
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [codeInfo, setCodeInfo] = useState(null);
  const [issuingCode, setIssuingCode] = useState(false);
  const [opening, setOpening] = useState(null);
  const checkedThisLoginRef = useRef(false);

  // Single source-of-truth fetch. Called once per login.
  const fetchGate = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/telegram/gate-status`);
      setStatus({
        loading: false,
        blocked: !!data?.blocked,
        reason: data?.reason,
        needsLink: !!data?.needsLink,
        missingGroups: Array.isArray(data?.missingGroups) ? data.missingGroups : [],
        joinedGroups:  Array.isArray(data?.joinedGroups)  ? data.joinedGroups  : [],
        unknownGroups: Array.isArray(data?.unknownGroups) ? data.unknownGroups : [],
        joinedCount:   typeof data?.joinedCount === 'number' ? data.joinedCount : 0,
        totalRequired: typeof data?.totalRequired === 'number' ? data.totalRequired : 0,
        botUsername: data?.botUsername,
      });
    } catch (e) {
      // If the gate endpoint fails (e.g. backend hiccup) we fail-OPEN so
      // the app stays usable. The next page load will re-check anyway.
      // eslint-disable-next-line no-console
      console.debug('Telegram gate check failed:', e?.message);
      setStatus({ loading: false, blocked: false, missingGroups: [] });
    }
  }, []);

  // Fire ONCE per (user.id, mount) — i.e. once per login. We don't poll.
  useEffect(() => {
    if (!user || checkedThisLoginRef.current) return;
    checkedThisLoginRef.current = true;
    fetchGate();
  }, [user, fetchGate]);

  // Reset when the user logs out so the next login re-runs the check.
  const prevUserIdRef = useRef(null);
  useEffect(() => {
    const currentId = user?.id || null;
    const prev = prevUserIdRef.current;
    prevUserIdRef.current = currentId;
    // Only reset when transitioning from logged-in → logged-out
    if (prev && !currentId) {
      checkedThisLoginRef.current = false;
      setStatus({ loading: true, blocked: false, missingGroups: [] });
    }
  }, [user]);

  // ── Link Telegram flow (mirrors TelegramLinkButton, inlined so we
  // can render it in the full-screen gate). Poll every 4s while the
  // dialog is open; close + re-check the gate as soon as linked.
  const openLinkDialog = async () => {
    setIssuingCode(true);
    try {
      const { data } = await axios.get(`${API}/telegram/my-link-code`);
      if (data?.linked) {
        await fetchGate();
        return;
      }
      setCodeInfo(data);
      setLinkDialogOpen(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || t('Could not generate link code'));
    } finally {
      setIssuingCode(false);
    }
  };

  useEffect(() => {
    if (!linkDialogOpen) return undefined;
    const timer = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/telegram/my-link-status`);
        if (data?.linked) {
          clearInterval(timer);
          setLinkDialogOpen(false);
          toast.success(t('Telegram linked — checking groups…'));
          fetchGate();
        }
      } catch (_) {/* ignore polling errors */}
    }, 4000);
    return () => clearInterval(timer);
  }, [linkDialogOpen, fetchGate, t]);

  const openGroupInvite = (g) => {
    setOpening(g.id);
    window.open(g.inviteLink, '_blank', 'noopener,noreferrer');
    setTimeout(() => setOpening(null), 800);
  };

  const refreshGate = async () => {
    setStatus(s => ({ ...s, loading: true }));
    await fetchGate();
  };

  // ── Render gates ───────────────────────────────────────────────
  // Don't gate the unauthenticated state — let /login render freely.
  if (!user) return children;

  // While checking, render the app behind a thin loader. Avoids a
  // jarring full-screen flash for the common case (already-linked
  // users who pass the gate within ~200ms).
  if (status.loading) {
    return (
      <>
        {children}
        <div className="fixed top-3 right-3 z-[60] flex items-center gap-2 rounded-full bg-white shadow border border-border px-3 py-1.5 text-xs text-muted-foreground"
             data-testid="telegram-gate-loading">
          <Loader2 className="w-3 h-3 animate-spin" />
          {t('Checking Telegram…')}
        </div>
      </>
    );
  }

  if (!status.blocked) return children;

  // ── BLOCKED — full-screen page ─────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 bg-gradient-to-br from-amber-50 via-white to-amber-100 overflow-y-auto"
         data-testid="telegram-gate-blocker">
      <div className="min-h-full flex items-start sm:items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-amber-200 overflow-hidden">
          <div className="bg-amber-500 text-white px-6 py-5 flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-white/20 flex items-center justify-center">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-base font-bold tracking-tight">{t('Telegram link required')}</p>
              <p className="text-xs opacity-90 mt-0.5">
                {t('To continue using Value Plus, please complete the steps below.')}
              </p>
            </div>
            <LanguageToggle />
          </div>

          <div className="p-6 space-y-5">
            {/* Step 1: Link Telegram (only if missing) */}
            <div className={`rounded-lg border p-4 ${status.needsLink ? 'border-amber-300 bg-amber-50' : 'border-emerald-200 bg-emerald-50'}`}>
              <div className="flex items-start gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold shrink-0 ${status.needsLink ? 'bg-amber-500' : 'bg-emerald-500'}`}>
                  {status.needsLink ? '1' : <CheckCircle2 className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm">
                    {status.needsLink ? t('Link your personal Telegram') : t('Telegram linked')}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {status.needsLink
                      ? t('One-time setup — connect your Telegram account to Value Plus so we can deliver alerts to you.')
                      : t('Your Telegram account is connected.')}
                  </p>
                  {status.needsLink && (
                    <Button
                      onClick={openLinkDialog}
                      disabled={issuingCode}
                      className="mt-3 bg-amber-600 hover:bg-amber-700 text-white"
                      size="sm"
                      data-testid="gate-link-telegram-btn"
                    >
                      {issuingCode ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                      {t('Link my Telegram')}
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Step 2: Join required groups */}
            {(() => {
              const joined = status.joinedCount || 0;
              const total = status.totalRequired || (status.missingGroups.length + joined + (status.unknownGroups?.length || 0));
              const remaining = status.missingGroups.length + (status.unknownGroups?.length || 0);
              const showProgress = total > 1;
              return (
                <>
                  {/* Progress banner — reassures the user of what's already done */}
                  {showProgress && (
                    <div className="rounded-lg bg-emerald-50 border border-emerald-300 p-3"
                         data-testid="gate-progress-banner">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-500 flex items-center justify-center text-white font-bold text-sm shrink-0">
                          {joined}/{total}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-emerald-900">
                            {joined === 0
                              ? t('Join {n} Telegram group(s) to unlock the app', { n: total })
                              : joined >= total
                                ? t('Almost there — just verifying…')
                                : remaining === 1
                                  ? t("You've joined {a} of {b} groups. Just 1 more to unlock the app!", { a: joined, b: total })
                                  : t("You've joined {a} of {b} groups. {n} more to unlock the app!", { a: joined, b: total, n: remaining })}
                          </p>
                          <div className="mt-1.5 h-1.5 bg-emerald-100 rounded-full overflow-hidden">
                            <div className="h-full bg-emerald-500 transition-all"
                                 style={{ width: `${total ? (joined / total) * 100 : 0}%` }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Already-joined groups — visible reassurance ✅ */}
                  {status.joinedGroups && status.joinedGroups.length > 0 && (
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-3"
                         data-testid="gate-joined-groups">
                      <p className="text-xs font-semibold text-emerald-900 uppercase tracking-wider mb-1.5">
                        {t("✅ Groups you've joined")}
                      </p>
                      <ul className="space-y-1">
                        {status.joinedGroups.map(g => (
                          <li key={g.id} className="flex items-center gap-2 text-sm"
                              data-testid={`gate-joined-group-${g.id}`}>
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                            <span className="text-emerald-900">{g.name}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Unknown-verification groups — bot can't verify (usually needs admin rights) */}
                  {status.unknownGroups && status.unknownGroups.length > 0 && (
                    <div className="rounded-lg border border-blue-300 bg-blue-50 p-4"
                         data-testid="gate-unknown-groups">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white shrink-0">
                          <ShieldAlert className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-sm text-blue-900">
                            {t("Membership couldn't be verified ({n})", { n: status.unknownGroups.length })}
                          </p>
                          <p className="text-xs text-blue-800 mt-0.5 mb-2">
                            {t("The bot needs admin rights in these groups to verify who's joined. You may have already joined — please contact your admin.")}
                          </p>
                          <ul className="space-y-1">
                            {status.unknownGroups.map(g => (
                              <li key={g.id} className="text-sm text-blue-900 pl-2 border-l-2 border-blue-300"
                                  data-testid={`gate-unknown-group-${g.id}`}>
                                {g.name}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
            {status.missingGroups.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                    {status.needsLink ? '2' : '1'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm">
                      {status.joinedCount > 0 && status.missingGroups.length === 1
                        ? t('Join this last group')
                        : status.joinedCount > 0
                          ? t('Join these {n} remaining groups', { n: status.missingGroups.length })
                          : t('Join your Telegram group(s) ({n})', { n: status.missingGroups.length })}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5 mb-3">
                      {t('Your admin has added you. Tap each card to join.')}
                      {status.missingGroups[0]?.linkedTelegramUsername && (
                        <span className="block mt-1 text-amber-800 font-medium">
                          {t('⚠️ Join with your linked account')}
                          {' '}{t('(@{u}) — joining with a different account will get you removed automatically.', { u: status.missingGroups[0].linkedTelegramUsername })}
                        </span>
                      )}
                    </p>

                    <div className="space-y-2">
                      {status.missingGroups.map(g => (
                        <div key={g.id}
                             className="bg-white rounded-md border border-amber-200 p-3"
                             data-testid={`gate-pending-group-${g.id}`}>
                          <div className="flex items-start gap-2.5">
                            <Users className="w-4 h-4 text-amber-700 mt-0.5 shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium break-words">{g.name}</p>
                              {g.description && (
                                <p className="text-xs text-muted-foreground mt-0.5 break-words">{g.description}</p>
                              )}
                            </div>
                          </div>
                          {g.inviteLink ? (
                            <Button
                              onClick={() => openGroupInvite(g)}
                              disabled={opening === g.id}
                              size="sm"
                              className="w-full mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                              data-testid={`gate-join-group-btn-${g.id}`}
                            >
                              {opening === g.id ? (
                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('Opening Telegram…')}</>
                              ) : (
                                <><ExternalLink className="w-4 h-4 mr-2" />{t('Join group on Telegram')}</>
                              )}
                            </Button>
                          ) : (
                            <p className="text-[11px] text-muted-foreground italic mt-2">
                              {t('Invite link not configured yet — contact your admin.')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Footer actions */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-2 border-t border-border">
              <Button
                onClick={refreshGate}
                variant="outline"
                size="sm"
                className="flex-1"
                data-testid="gate-refresh-btn"
              >
                <Loader2 className={`w-4 h-4 mr-2 ${status.loading ? 'animate-spin' : ''}`} />
                {t("I've completed the steps — check again")}
              </Button>
              <Button
                onClick={() => { logout(); toast.success(t('Logged out')); }}
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                data-testid="gate-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                {t('Log out')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Link-code dialog (mirrors TelegramLinkButton) */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="gate-link-dialog">
          <DialogHeader>
            <DialogTitle>{t('Link your Telegram')}</DialogTitle>
            <DialogDescription>
              {t('Connect your Telegram account to receive alerts and unlock the app.')}
            </DialogDescription>
          </DialogHeader>
          {codeInfo && (
            <div className="space-y-4">
              <div className="bg-muted/30 rounded p-3 text-center font-mono text-xl tracking-widest"
                   data-testid="gate-link-code">
                {codeInfo.code}
              </div>
              <Button
                className="w-full"
                onClick={() => window.open(codeInfo.deepLink, '_blank', 'noopener,noreferrer')}
                data-testid="gate-link-open-btn"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('Open Telegram & link automatically')}
              </Button>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>{t("If the button doesn't work, open Telegram → search {bot} → send:", { bot: `@${codeInfo.botUsername}` })} <code className="bg-muted px-1 rounded">/link {codeInfo.code}</code></p>
                <p className="pt-1 italic">{t('Code expires in 30 minutes · single use')}</p>
              </div>
              <div className="flex items-center gap-2 pt-1 border-t">
                <Loader2 className="w-3 h-3 animate-spin text-muted-foreground" />
                <span className="text-xs text-muted-foreground">{t('Waiting for you to finish in Telegram…')}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TelegramGate;
